import { create } from 'zustand'

type Groups = Map<string, string[]>

type GroupRestrictions = Map<
  string,
  {
    minSelection: number
    maxSelection: number
  }
>

interface SKUSelectionStore {
  groups: Groups
  groupRestrictions: GroupRestrictions

  selectSKU: (railSlug: string, sku: string) => void
  getSelectedSKUs: () => string[]
  setupGroup: (groupId: string, minSelection: number, maxSelection: number) => void
}

export const useSKUSelectionStore = create<SKUSelectionStore>((set, get) => ({
  groups: new Map<string, string[]>(),
  groupRestrictions: new Map<
    string,
    {
      minSelection: number
      maxSelection: number
    }
  >(),
  selectSKU(groupId: string, sku: string) {
    const group = get().groups?.get(groupId) ?? []
    let newGroup = group.includes(sku) ? group.filter((value) => value !== sku) : [...group, sku]

    const maxSelection = get().groupRestrictions?.get(groupId)?.maxSelection ?? 1

    if (newGroup.length > maxSelection) {
      let remainingSelections = maxSelection - 1 // Reserve one spot for the newly selected item

      // Unselect previously selected items
      newGroup = newGroup
        .map((item) => {
          if (item !== sku && --remainingSelections < 0) {
            return undefined
          }
          return item
        })
        .filter(Boolean) as string[]
    }

    set((state) => {
      const groups = new Map(state.groups).set(groupId, newGroup)

      return {
        groups,
      }
    })
  },
  getSelectedSKUs() {
    const skus = [...get().groups?.values()].flat()

    return skus
  },
  setupGroup(groupId, minSelection, maxSelection) {
    set((state) => {
      const groupRestrictions = new Map(state.groupRestrictions).set(groupId, {
        minSelection,
        maxSelection,
      })
      const groups = new Map(state.groups).set(groupId, [])

      return {
        groups,
        groupRestrictions,
      }
    })
  },
}))

export const isSelectionStateValid = (groups: Groups, groupRestrictions: GroupRestrictions) => {
  const isValid = [...groups.entries()].every(([groupId, group]) => {
    const min = groupRestrictions?.get(groupId)?.minSelection ?? 1
    const max = groupRestrictions?.get(groupId)?.maxSelection ?? 1

    const isGroupValid = group.length >= min && group.length <= max

    return isGroupValid
  })

  return isValid
}
